import { format } from "date-fns";
import { Link } from "react-router-dom";

import type { ScheduledSchedule } from "@/api/schedule/models";
import { Button } from "@/components/common/Button";
import { Icon } from "@/components/common/Icon";
import { Table } from "@/components/common/Table";
import { useDepartmentLink } from "@/hooks/common/use-department-link";
import type { TableFilterControls, TableSortingControls } from "@/hooks/common/table/use-table-controls";
import type { PaginationMetadata } from "@/interfaces/api";
import { buildRoute, RoutePathEnum } from "@/routes/paths";

export interface ScheduleTableColumn {
  key: string;
  label: string;
  render: (schedule: ScheduledSchedule) => React.ReactNode;
}

interface ScheduleTableProps {
  schedules: ScheduledSchedule[];
  columns: ScheduleTableColumn[];
  pagination: PaginationMetadata;
  isLoading: boolean;
  showPresence: boolean;
  categoryFilter: TableFilterControls;
  photoServiceFilter: TableFilterControls;
  lastStageFilter: TableFilterControls;
  statusFilter: TableFilterControls;
  timeSort: TableSortingControls;
  canUpdatePresence: boolean;
  pendingScheduleId: string | null;
  onPageChange: (page: number) => void;
  onPresenceChange: (schedule: ScheduledSchedule) => void;
}

const scheduleTypeLabels: Record<ScheduledSchedule["scheduleType"], string> = {
  PHOTO_RENEWAL: "Renovação de fotos",
  NEW_CANDIDATE: "Novo candidato",
};

const scheduleStageLabels: Record<NonNullable<ScheduledSchedule["lastStage"]>, string> = {
  SCHEDULED: "Agendados",
  RECEPTION: "Recepção",
  PHOTOGRAPHY: "Fotografia",
  PRODUCTION: "Produção",
  ART_PRODUCTION: "Produção artística",
};

const scheduleStatusLabels: Record<ScheduledSchedule["currentStatus"], string> = {
  IN_PROGRESS: "Em atendimento",
  PAUSED: "Pausado",
  CANCELED: "Cancelado",
  COMPLETED: "Concluído",
  AWAITING: "Aguardando",
};

const scheduleStatusClasses: Record<ScheduledSchedule["currentStatus"], string> = {
  IN_PROGRESS: "bg-[rgba(253,203,105,0.1)] text-[rgba(199,136,14,1)]",
  PAUSED: "bg-[rgba(255,254,215,1)] text-[rgba(190,187,75,1)]",
  AWAITING: "bg-[rgba(152,160,166,0.1)] text-[rgba(152,160,166,1)]",
  CANCELED: "bg-[rgba(227,80,80,0.1)] text-[rgba(227,80,80,1)]",
  COMPLETED: "bg-[rgba(89,206,129,0.1)] text-[rgba(89,206,129,1)]",
};

const photoServiceLabels: Record<NonNullable<ScheduledSchedule["photoService"]>, string> = {
  PHOTO_MATERIAL: "Material fotográfico",
  PHOTOGENY: "Fotogenia",
};

export const scheduledScheduleColumns: ScheduleTableColumn[] = [
  {
    key: "talent",
    label: "Nome/código do talento",
    render: (schedule) => (
      <div>
        <p className="font-medium">{schedule.fullName}</p>
        <span className="text-sm text-gray">{schedule.talentCode ?? "-"}</span>
      </div>
    ),
  },
  {
    key: "responsible",
    label: "Responsável",
    render: (schedule) => schedule.responsibleName ?? "-",
  },
  {
    key: "category",
    label: "Categoria",
    render: (schedule) => scheduleTypeLabels[schedule.scheduleType],
  },
  {
    key: "time",
    label: "Horário",
    render: (schedule) => format(new Date(schedule.scheduleDate), "HH:mm"),
  },
];

export const operationalScheduleColumns: ScheduleTableColumn[] = [
  ...scheduledScheduleColumns.slice(0, 3),
  {
    key: "time",
    label: "Horário",
    render: (schedule) => (
      <div>
        <p>{format(new Date(schedule.scheduleDate), "HH:mm")}</p>
        <span className="text-sm text-gray">
          Chegada: {schedule.arrivalDate ? format(new Date(schedule.arrivalDate), "HH'h'mm") : "-"}
        </span>
      </div>
    ),
  },
  {
    key: "lastStage",
    label: "Última etapa",
    render: (schedule) => (
      <div>
        <p>{schedule.lastStage ? scheduleStageLabels[schedule.lastStage] : "-"}</p>
        <span className="text-sm text-gray">
          {schedule.lastStageTime ? format(new Date(schedule.lastStageTime), "HH:mm") : "-"}
        </span>
      </div>
    ),
  },
  {
    key: "status",
    label: "Situação atual",
    render: (schedule) => (
      <span
        className={`inline-block rounded-full px-2 py-1 font-medium ${scheduleStatusClasses[schedule.currentStatus]}`}
      >
        {scheduleStatusLabels[schedule.currentStatus]}
      </span>
    ),
  },
];

export const photographyScheduleColumns: ScheduleTableColumn[] = [
  ...operationalScheduleColumns.slice(0, 4),
  {
    key: "service",
    label: "Serviço",
    render: (schedule) => (schedule.photoService ? photoServiceLabels[schedule.photoService] : "-"),
  },
  ...operationalScheduleColumns.slice(4),
];

const operationalScheduleColumnsWithArrival: ScheduleTableColumn[] = [
  ...scheduledScheduleColumns.slice(0, 3),
  {
    key: "time",
    label: "Horário",
    render: (schedule) => (
      <div>
        <p>{format(new Date(schedule.scheduleDate), "HH:mm")}</p>
        <span className="text-sm text-gray">
          Chegada: {schedule.arrivalDate ? format(new Date(schedule.arrivalDate), "HH'h'mm") : "-"}
        </span>
      </div>
    ),
  },
  ...operationalScheduleColumns.slice(4),
];

export const receptionScheduleColumns = operationalScheduleColumnsWithArrival;
export const productionScheduleColumns = receptionScheduleColumns;
export const artisticProductionScheduleColumns = operationalScheduleColumnsWithArrival;

export const ScheduleTable = ({
  schedules,
  columns,
  pagination,
  isLoading,
  showPresence,
  categoryFilter,
  photoServiceFilter,
  lastStageFilter,
  statusFilter,
  timeSort,
  canUpdatePresence,
  pendingScheduleId,
  onPageChange,
  onPresenceChange,
}: ScheduleTableProps) => {
  const buildDepartmentLink = useDepartmentLink();

  return (
    <Table>
      <Table.Content isLoading={isLoading} dragScroll>
        <Table.Header>
          {showPresence && <Table.Column>Presença</Table.Column>}
          {columns.map((column) => {
            if (column.key === "category") {
              return (
                <Table.Column key={column.key} filter={categoryFilter}>
                  {column.label}
                </Table.Column>
              );
            }

            if (column.key === "service") {
              return (
                <Table.Column key={column.key} filter={photoServiceFilter}>
                  {column.label}
                </Table.Column>
              );
            }

            if (column.key === "lastStage") {
              return (
                <Table.Column key={column.key} filter={lastStageFilter}>
                  {column.label}
                </Table.Column>
              );
            }

            if (column.key === "status") {
              return (
                <Table.Column key={column.key} filter={statusFilter}>
                  {column.label}
                </Table.Column>
              );
            }

            if (column.key === "time") {
              return (
                <Table.Column key={column.key} sort={timeSort}>
                  {column.label}
                </Table.Column>
              );
            }

            return <Table.Column key={column.key}>{column.label}</Table.Column>;
          })}
          <Table.ActionsColumn />
        </Table.Header>
        <Table.Body>
          {schedules.map((schedule) => {
            const talentLink = buildDepartmentLink(
              buildRoute(RoutePathEnum.SCHEDULE_TALENT_DATA, { scheduleId: schedule.scheduleId }),
            );
            const canMarkPresence = schedule.currentStage === "SCHEDULED" && !schedule.isPresent;
            const canUnmarkPresence = schedule.currentStage === "RECEPTION" && schedule.isPresent;
            const presenceActionLabel = canUpdatePresence
              ? canMarkPresence
                ? "Confirmar"
                : canUnmarkPresence
                  ? "Presente"
                  : null
              : null;

            return (
              <Table.Row key={schedule.scheduleId}>
                {showPresence && (
                  <Table.Cell unwrapped className="!border-x-0 !border-t-0">
                    {presenceActionLabel && (
                      <Button
                        variant="secondary"
                        fit
                        className={
                          canUnmarkPresence ? "!border-[#59CE81] !text-[#59CE81] hover:!bg-[#59CE81]/10" : undefined
                        }
                        isLoading={pendingScheduleId === schedule.scheduleId}
                        onClick={() => onPresenceChange(schedule)}
                      >
                        {canUnmarkPresence && <Icon name="CheckCircle" className="!text-[#59CE81]" />}
                        {presenceActionLabel}
                      </Button>
                    )}
                  </Table.Cell>
                )}
                {columns.map((column) => (
                  <Table.Cell key={column.key} unwrapped className="!border-x-0 !border-t-0">
                    {column.render(schedule)}
                  </Table.Cell>
                ))}
                <Table.Cell unwrapped className="!border-x-0 !border-t-0 px-4 text-center">
                  {talentLink && (
                    <Link to={talentLink} aria-label={`Abrir perfil de ${schedule.fullName}`}>
                      <Icon name="KeyboardArrowRight" />
                    </Link>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Content>

      <Table.Pagination
        {...pagination}
        totalVisible={schedules.length}
        isLoading={isLoading}
        onPageChange={onPageChange}
      />
    </Table>
  );
};
